import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { type Provider, providerStartUrl, startEmailLogin } from "@/auth/api";
import { providersQuery } from "@/auth/queries";
import { safeRedirect } from "@/auth/redirect";
import { AuthLayout, Notice } from "@/components/AuthLayout";
import { isRateLimited } from "@/content/loader";
import { fr } from "@/i18n/fr";

const APPLE =
	"M16.5 12.5c0-2 1.6-3 1.7-3.1-1-1.4-2.4-1.6-2.9-1.6-1.3-.1-2.4.7-3 .7s-1.6-.7-2.6-.7C8.3 7.9 7 8.7 6.3 9.9c-1.5 2.6-.4 6.4 1.1 8.5.7 1 1.5 2.2 2.6 2.1 1-.1 1.5-.7 2.7-.7s1.6.7 2.7.7 1.8-1 2.5-2.1c.8-1.2 1.1-2.3 1.1-2.4-.1 0-2.5-1-2.5-3.5zM14.6 6c.6-.7.9-1.6.8-2.5-.8 0-1.8.5-2.3 1.2-.5.6-1 1.5-.8 2.4.9.1 1.8-.4 2.3-1.1z";
const GOOGLE =
	"M12 11v2.5h4.2c-.2 1.1-1.3 3.2-4.2 3.2a4.7 4.7 0 0 1 0-9.4c1.3 0 2.2.6 2.7 1l1.9-1.8A7.3 7.3 0 0 0 12 4.5a7.5 7.5 0 1 0 0 15c4.3 0 7.2-3 7.2-7.3 0-.5 0-.8-.1-1.2z";

const PROVIDERS: { id: Provider; label: string; glyph: string }[] = [
	{ id: "google", label: fr.login.google, glyph: GOOGLE },
	{ id: "apple", label: fr.login.apple, glyph: APPLE },
];

function Glyph({ d }: { d: string }) {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true" className="size-5 fill-current">
			<path d={d} />
		</svg>
	);
}

/** Why the last attempt to send a link failed. */
function sendError(error: unknown): string {
	if (isRateLimited(error) && error.retryAfterMs)
		return fr.login.wait(Math.ceil(error.retryAfterMs / 1000));
	return fr.login.sendError;
}

/**
 * Login page (`/connexion?redirect=`): Google, Apple or a magic link by e-mail. Google and Apple
 * are full-page trips through the API (`/auth/{provider}/start`), which come back to `redirect`,
 * or here with `?error=` when they fail. The magic link opens `/connexion/verifier`.
 */
export default function Login() {
	const search = useSearch({ from: "/connexion" });
	const next = safeRedirect(search.redirect);
	const providers = useQuery(providersQuery());
	const [mail, setMail] = useState("");
	const [invalid, setInvalid] = useState(false);
	const [sent, setSent] = useState<string | null>(null);
	const send = useMutation({
		mutationFn: (email: string) => startEmailLogin(email, next),
		onSuccess: (_, email) => setSent(email),
	});
	useEffect(() => {
		document.title = `${fr.signIn} · ${fr.appTitle}`;
	}, []);
	const submit = (e: FormEvent) => {
		e.preventDefault();
		const email = mail.trim();
		const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
		setInvalid(!ok);
		if (ok) send.mutate(email);
	};
	const providerError = search.error
		? (fr.login.errors[search.error] ?? fr.login.failed)
		: null;
	// shown while the list loads: a provider disappears only once known to be off
	const offered = PROVIDERS.filter(({ id }) => providers.data?.[id] !== false);

	return (
		<AuthLayout title={fr.login.title} lead={fr.login.lead}>
			{providerError && !sent && <Notice tone="danger">{providerError}</Notice>}
			{sent ? (
				<div className="grid gap-3">
					<Notice tone="ok">{fr.login.sent(sent)}</Notice>
					{send.isError && (
						<Notice tone="danger">{sendError(send.error)}</Notice>
					)}
					<button
						type="button"
						className="ui-btn-ghost"
						disabled={send.isPending}
						onClick={() => send.mutate(sent)}
					>
						{send.isPending ? fr.login.sending : fr.login.resend}
					</button>
					<button
						type="button"
						className="ui-btn-ghost"
						onClick={() => {
							send.reset();
							setSent(null);
						}}
					>
						{fr.login.otherMail}
					</button>
				</div>
			) : (
				<form onSubmit={submit} className="grid gap-3" noValidate>
					{offered.map(({ id, label, glyph }) => (
						<a
							key={id}
							href={providerStartUrl(id, next)}
							className="ui-btn w-full justify-start gap-3 no-underline"
						>
							<Glyph d={glyph} />
							<span className="mr-5 flex-1 text-center">{label}</span>
						</a>
					))}
					{offered.length > 0 && (
						<div className="flex items-center gap-3 text-fg-muted text-xs before:h-px before:flex-1 before:bg-line after:h-px after:flex-1 after:bg-line">
							{fr.login.orMail}
						</div>
					)}
					<label className="grid gap-2">
						<span className="sr-only">{fr.login.mailLabel}</span>
						<span className={`ui-field ${invalid ? "border-danger" : ""}`}>
							<Mail
								aria-hidden="true"
								className="size-5 shrink-0 text-fg-muted"
								strokeWidth={1.75}
							/>
							<input
								type="email"
								name="email"
								autoComplete="email"
								placeholder={fr.login.mailPlaceholder}
								value={mail}
								onChange={(e) => setMail(e.target.value)}
								className="h-full min-w-0 flex-1 bg-transparent outline-none placeholder:text-fg-muted"
							/>
						</span>
						{invalid && (
							<span className="text-[13px] text-danger leading-[18px]">
								{fr.login.mailInvalid}
							</span>
						)}
					</label>
					{send.isError && (
						<Notice tone="danger">{sendError(send.error)}</Notice>
					)}
					<button
						type="submit"
						className="ui-btn-primary w-full"
						disabled={send.isPending}
					>
						<Mail className="size-5" aria-hidden="true" />
						{send.isPending ? fr.login.sending : fr.login.sendLink}
					</button>
				</form>
			)}
		</AuthLayout>
	);
}
